import React, { useEffect, useState } from "react";
import Button from "/src/helpers/components/button.jsx";
import { useApi } from "/src/helpers/Hooks/useApi";
import { CheckIcon } from "/src/helpers/components/upsellIcons.jsx";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INPUT =
    "w-full px-4 py-3 border border-input_border rounded-lg text-primary placeholder-content_3 focus:outline-none focus:ring-2 focus:ring-emp focus:border-transparent transition";

const GiftIcon = ({ size = 20 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z" />
    </svg>
);

/**
 * Agent incentive card: shown only to the travel agent who placed the booking (the API decides).
 * Lets them claim the booking's incentive if they didn't tick "auto-claim" at checkout, and shows
 * the claim's progress (claimed -> issued / cancelled) afterwards.
 */
const IncentiveCard = () => {
    const api = useApi();
    const [status, setStatus] = useState(null);
    const [form, setForm] = useState({ agentName: "", agentEmail: "" });
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let live = true;
        api.getIncentive().then((s) => {
            if (!live || !s) return;
            setStatus(s);
            setForm((f) => ({ ...f, agentName: f.agentName || s.suggestedName || "" }));
        });
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
    }, []);

    if (!status?.show) return null;

    const claim = status.claim;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.agentName.trim()) return setError("Please enter your name.");
        if (!EMAIL_PATTERN.test(form.agentEmail.trim())) return setError("Please enter a valid email address.");
        setError(null);
        setSubmitting(true);
        const result = await api.claimIncentive({ agentName: form.agentName.trim(), agentEmail: form.agentEmail.trim() });
        setSubmitting(false);
        if (!result) {
            setError("Sorry, something went wrong. Please try again later.");
            return;
        }
        if (result.succeeded || result.claim) {
            setStatus({ ...status, ...result, canClaim: false });
            setMessage(result.message);
        } else {
            setError(result.message);
        }
    };

    return (
        <section className="mb-6 bg-white rounded-card shadow-card px-8 py-5">
            <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
                <div className="w-[2.375rem] h-[2.375rem] rounded-[0.625rem] bg-emp_soft text-emp_dark flex items-center justify-center shrink-0">
                    <GiftIcon />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-poppins font-bold text-primary leading-tight">
                        Agent incentive{status.amountText ? <span className="text-emp_dark"> {status.amountText}</span> : null}
                    </h2>

                    {claim ? (
                        <div className="text-[0.84375rem] text-content_2 mt-1 space-y-0.5">
                            {message && <p className="text-green font-semibold">{message}</p>}
                            {claim.status === "Issued" && (
                                <p className="inline-flex items-center gap-1 text-green font-semibold">
                                    <CheckIcon size={12} /> {claim.amountText} gift card emailed to {claim.agentEmail} on {claim.issuedDate}
                                </p>
                            )}
                            {claim.status === "Claimed" && !message && (
                                <p>
                                    Claimed by {claim.agentName} on {claim.claimedDate}. Your {claim.amountText} gift card will be
                                    emailed to {claim.agentEmail} from {claim.issueFromDate}, as long as the booking is still live.
                                </p>
                            )}
                            {claim.status === "Cancelled" && (
                                <p className="text-orange font-semibold">
                                    This incentive claim was cancelled{claim.cancelReason ? `: ${claim.cancelReason}` : "."}
                                </p>
                            )}
                        </div>
                    ) : status.canClaim ? (
                        <form onSubmit={submit} className="mt-2" noValidate>
                            <p className="text-[0.84375rem] text-content_2 mb-3">
                                Claim your {status.amountText} incentive for this booking
                                {status.claimByDate ? ` by ${status.claimByDate}` : ""}. We'll email the gift card to you once
                                the waiting period has passed, as long as the booking is still live.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label htmlFor="incentive-name" className="block text-sm font-medium text-primary mb-1">Your name</label>
                                    <input id="incentive-name" type="text" autoComplete="name" className={INPUT}
                                           value={form.agentName} onChange={(e) => setForm({ ...form, agentName: e.target.value })} />
                                </div>
                                <div>
                                    <label htmlFor="incentive-email" className="block text-sm font-medium text-primary mb-1">Send the gift card to</label>
                                    <input id="incentive-email" type="email" autoComplete="email" className={INPUT} placeholder="you@agency.co.uk"
                                           value={form.agentEmail} onChange={(e) => setForm({ ...form, agentEmail: e.target.value })} />
                                </div>
                            </div>
                            {error && <p role="alert" className="text-sm text-payment font-semibold mb-3">{error}</p>}
                            <Button type="submit" disabled={submitting}>
                                {submitting ? "Claiming…" : `Claim my ${status.amountText} incentive`}
                            </Button>
                        </form>
                    ) : (
                        <p className="text-[0.84375rem] text-content_2 mt-1">{status.message}</p>
                    )}
                </div>
            </div>
        </section>
    );
};

export default IncentiveCard;
